import * as THREE from "three";
import { CSS3DObject } from "three/examples/jsm/renderers/CSS3DRenderer";
import TextBoxMethods from "./TextBoxMethods";
import debug from "debug";

const log = debug("app:txt");

/**
 * Class responsible for creating and managing text box geometries in a Three.js scene.
 * @category Components
 * @subcategory TextBox
 */
export default class TextBoxGeom {
  /**
   * Creates an instance of TextBoxGeom.
   * @param {Object} experience - The main experience object containing scene and other dependencies.
   */
  constructor(experience) {
    this.experience = experience;
    this.scene = experience.scene;
    this.textBoxMethods = new TextBoxMethods();
  }

  cleanup() {
    if (this.cssObject) {
        // Remove the CSS3DObject from its parent container
        const css3dContainer = document.getElementById("css3d-container");
        if (css3dContainer && this.cssObject.element) {
            css3dContainer.removeChild(this.cssObject.element);
        }

        // Remove CSS3DObject from the scene
        if (this.cssObject.parent) {
            this.cssObject.parent.remove(this.cssObject);
        }
        
        this.cssObject = null;
    }

    // Remove all text box groups from the scene
    const groups = this.scene.children.filter(obj => obj.name === "XMO");
    groups.forEach(group => {
        this.scene.remove(group);
        group.traverse(obj => {
            if (obj.geometry) obj.geometry.dispose();
            if (obj.material) obj.material.dispose();
        });
    });

    this.textBoxMethods = null;
    this.scene = null;
    this.experience = null;

    console.log("✅ TextBoxGeom cleaned up.");
}

  /**
   * Creates a 3D text box object and adds it to the scene.
   * @param {Object} userData - Configuration data for the text box.
   * @param {number} scale - The scale factor for the text box.
   */
  createTextBoxObject(userData, scale) {
    log("Creating text box, checking latest transparency...");

    // Retrieve latest transparency from localStorage (or default to 1)
    const latestAlpha = localStorage.getItem("textboxAlpha") || "1";

    const textBoxElement = this.createCssTextBox(userData, scale);
    this.cssObject = new CSS3DObject(textBoxElement);
    this.cssObject.name = "CSS3DObject";
    this.cssObject.rotation.x = -Math.PI / 2;
    this.cssObject.scale.set(1 / scale, 1 / scale, 1 / scale);

    this.cssObject.element.contentEditable = false;
    this.cssObject.element.style.pointerEvents = "none";

    // Apply latest transparency from slider
    this.cssObject.element.style.opacity = latestAlpha;
    userData.alpha = latestAlpha; // Ensure userData also stores it

    log(`✅ Applied latest transparency: ${latestAlpha}`);

    const css3dContainer = document.getElementById("css3d-container");
    css3dContainer.appendChild(this.cssObject.element);

    const group = new THREE.Group();
    group.name = "XMO";
    group.userData = { ...userData };

    const planeMesh = this.textBoxMethods.createTextBoxMesh(userData.w, userData.h);
    group.position.set(userData.x, userData.y, userData.z);
    group.rotation.y = THREE.MathUtils.degToRad(userData.angle);
    group.add(planeMesh);
    group.add(this.cssObject);
    this.scene.add(group);
    
    // Use existing flipTextBoxes logic to handle camera view
    const cameraYDirection = Math.sign(this.experience.camera.instance.position.y);
    log("Creating text box with cameraYDirection:", cameraYDirection, "(positive = top view, negative = bottom view)");
    if (cameraYDirection < 0) {
      // Apply flipTextBoxes logic for bottom view
      group.rotation.x = Math.PI;
      group.position.y = -1;
    }
}

  // Create the CSS text box element with config data


  /**
   * Creates a styled CSS text box element based on the user data and scale.
   * @param {Object} userData - Configuration data for the text box.
   * @param {number} scale - The scale factor for the text box.
   * @returns {HTMLDivElement} The created CSS text box element.
   */
  createCssTextBox(userData, scale) {
    const textBox = document.createElement("div");
    this.textBoxMethods.applyStyles(textBox, userData, scale);

    textBox.style.margin = "0";
    textBox.style.padding = "0";
    textBox.style.transformOrigin = "center";
    textBox.style.backfaceVisibility = "visible";
    textBox.style.overflow = "hidden";
    textBox.style.whiteSpace = "pre-wrap";
    textBox.style.wordBreak = "break-word";
    textBox.contentEditable = false;
    textBox.style.pointerEvents = "none";
    textBox.style.zIndex = "10";

    // Apply stored transparency
    textBox.style.opacity = userData.alpha !== undefined ? userData.alpha : "1";
    
    return textBox;
}

}
